import { useNavigate, useParams } from "@tanstack/react-router";
import { useCallback, useState } from "react";

import HomeClient from "@/app/HomeClient";
import { MONOLITH_BOARD_STYLE } from "@/components/dashboard/monolithBoardStyle";
import JoinPageAuthModal from "@/components/JoinPageAuthModal";
import { Button } from "@/components/ui/Button";
import { useAuth } from "@/hooks/useAuth";
import { cn } from "@/lib/classNames";
import { usePublicCampaignJoinInfoQuery } from "@/lib/queries/campaignQueries";

export default function CampaignEditorPage() {
  const { shortId } = useParams({ from: "/campaign/$shortId" });
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [authJustCompleted, setAuthJustCompleted] = useState(false);

  // Only fetch the public campaign info while we need it for the unauthed
  // gate; signed-in users go straight to `HomeClient`, which loads the full
  // (auth-required) campaign record on its own.
  const publicJoinInfoQuery = usePublicCampaignJoinInfoQuery(
    shortId,
    !authLoading && !user && !authJustCompleted,
  );

  const handleAuthenticated = useCallback(() => {
    setAuthJustCompleted(true);
  }, []);

  if (user || authJustCompleted) {
    // Key on shortId so navigating between two `/campaign/<shortId>` URLs
    // (e.g. from the Campaigns modal "Open" button) remounts `HomeClient`.
    // Otherwise it stays mounted and its bootstrap effect early-returns
    // because `sessionReady` is already true for the previous campaign,
    // so the new campaign's data never loads.
    return <HomeClient key={shortId} entry={{ kind: "cloud-campaign", shortId }} />;
  }

  if (authLoading || publicJoinInfoQuery.isPending) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#f7f3eb]">
        <p className="text-sm uppercase tracking-[0.28em] text-neutral-600">Loading...</p>
      </div>
    );
  }

  const campaignName = publicJoinInfoQuery.data?.campaignName ?? null;

  if (publicJoinInfoQuery.isError || !campaignName) {
    return (
      <div className={cn("relative overflow-hidden", MONOLITH_BOARD_STYLE.shellClassName)}>
        <div className="flex min-h-screen items-center justify-center">
          <div className="max-w-md border-[3px] border-neutral-950 bg-[#fffaf1] p-8 shadow-[12px_12px_0_rgba(19,19,19,0.14)]">
            <p className="text-sm font-semibold uppercase tracking-[0.24em] text-neutral-600">
              Campaign not found
            </p>
            <p className="mt-3 text-sm leading-relaxed text-neutral-700">
              This campaign link may be invalid or the campaign no longer exists.
            </p>
            <Button
              onClick={() => navigate({ to: "/", replace: true })}
              variant="primary"
              chrome="frame"
              type="button"
              className="mt-6"
            >
              Go home
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={cn("relative overflow-hidden", MONOLITH_BOARD_STYLE.shellClassName)}>
      <div className="flex min-h-screen items-center justify-center">
        <JoinPageAuthModal
          mode="view"
          campaignName={campaignName}
          onAuthenticated={handleAuthenticated}
          onCancel={() => navigate({ to: "/", replace: true })}
        />
      </div>
    </div>
  );
}
